import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from 'formik';
import { t } from "i18next";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { SelectAvatar } from "@/shared/components/organisms/SelectAvatar";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { ButtonWhite } from "@/shared/components/atoms/ButtonWhite";
import { Badges } from "@/shared/components/molecules/Badges";
import { Select } from "@/shared/components/organisms/Select";
import { TextArea } from "@/shared/components/atoms/TextArea";
import ButtonAdd from "@/shared/components/atoms/ButtonAdd";
import { toasts } from "@/shared/utils/toasts";

// API
import {
  useSupportStaffQuery,
} from "@/app/users/users.api";
import {
  useLeadTasksQuery,
  useStoreTaskMutation,
  useAllTasksQuery,
  useStoreTicketMutation,
  useCompleteTaskMutation,
  useNotesQuery,
  useStoreNotesMutation,
} from "@/app/users/loyalty.api";

// SCHEMAS
import {
  initialValuesForm1,
  validationSchemaForm1,
  initialValuesForm2,
  validationSchemaForm2,
  initialValuesNotes,
  validationNotesSchema,
}  from "@/shared/schemas/loyaltyUser.schema";

type option = { id: string | null | number; name: string };

const LoyaltyUser = () => {
  const { id } = useParams();

  const [listStaff, setListStaff] = useState([]);
  const usersSupportStaff = useSupportStaffQuery({
    id
  });

  // TABS TASK
  const optionsTask: option[] = [
    { id: 0, name: t("lead.views.list_task_progress") },
    { id: 1, name: t("lead.views.list_tasks_complete") },
    { id: 2, name: t("lead.views.list_tasks_past") },
  ];
  const [selectTask, setSelectTask] = useState<option>(optionsTask[0]);

  const useLazyAllTasks = useAllTasksQuery({
    id,
    state: selectTask ? selectTask.id : 0
  });

  useEffect(() => {
    if (usersSupportStaff.isSuccess) {
      setListStaff(usersSupportStaff.data);
    }
  }, [usersSupportStaff])

  const [leadTasks, setLeadTasks] = useState([]);
  const useLazyLeadTasks = useLeadTasksQuery({
    id,
  });

  useEffect(() => {
    if (useLazyLeadTasks.isSuccess) {
      setLeadTasks(useLazyLeadTasks.data);
    }
  }, [useLazyLeadTasks])

  const [storeTicket, storeTicketResult] = useStoreTicketMutation();
  const [storeTask, storeTaskResult] = useStoreTaskMutation();
  const [storeNotes, storeNotesResult] = useStoreNotesMutation();

  // COMPLETE TASK
  const [completeTask, completeTaskResult] = useCompleteTaskMutation();

  useEffect(() => {
    if (completeTaskResult.isSuccess) {
      toasts.success(
        "Completada",
        "Tarea completada"
      );

      useLazyAllTasks.refetch();
    }
    if (completeTaskResult.isError) {
      toasts.danger(
        "No completada",
        "No se han podido registrar la incidencia"
      );
    }
  }, [ completeTaskResult]);
  
  const [notes, setNotes] = useState("");
  const notesQuery = useNotesQuery({
    id,
  });

  const [openDialogTask, setOpenDialogTask] = useState(false);
  const handleOpenDialogTask = () => setOpenDialogTask(!openDialogTask);

  const [titleDialogTask, setTitleDialogTask] = useState("");
  const [textButtonSubmitTask, setTextButtonSubmitTask] = useState("");
  const [formTask, setFormTask] = useState(0);


  const formikForm1 = useFormik({
    initialValues: initialValuesForm1,
    validationSchema: validationSchemaForm1,
    onSubmit: (values) => {
      storeTicket({
        ...values,
        client_id: id
      });
    },
  });

  useEffect(() => {
    if (storeTicketResult.isSuccess) {
      toasts.success(
        "Registro exitoso",
        "Incidencia registrada exitosamente"
      );

      formikForm1.resetForm();
      handleOpenDialogTask();
      setFormTask(0);
      useLazyAllTasks.refetch();
    }
    if (storeTicketResult.isError) {
      toasts.danger(
        "Error",
        "No se han podido registrar la incidencia"
      );

      formikForm1.resetForm();
      handleOpenDialogTask();
      setFormTask(0);
    }
  }, [ storeTicketResult]);

  const formikForm2 = useFormik({
    initialValues: initialValuesForm2,
    validationSchema: validationSchemaForm2,
    onSubmit: (values) => {
      storeTask({
        ...values,
        user_task_id: id,
      });
    },
  });

  useEffect(() => {
    if (storeTaskResult.isSuccess) {
      toasts.success(
        "Registro exitoso",
        "Tarea registrada exitosamente"
      );

      formikForm1.resetForm();
      handleOpenDialogTask();
      setFormTask(0);
      useLazyAllTasks.refetch();
    }
    if (storeTaskResult.isError) {
      toasts.danger(
        "Error",
        "No se han podido registrar la tarea"
      );

      formikForm2.resetForm();
      handleOpenDialogTask();
      setFormTask(0);
    }
  }, [ storeTaskResult]);

  const formikNotes = useFormik({
    initialValues: initialValuesNotes,
    validationSchema: validationNotesSchema,
    onSubmit: (values) => {
      storeNotes({
        notes: JSON.stringify(values),
        user_id: id,
      });
    },
  });

  useEffect(() => {
    if (notesQuery.isSuccess && notesQuery.data) {
      try {
        const parsedData = JSON.parse(notesQuery.data);
        formikNotes.setFieldValue("title", parsedData.title || "");
        formikNotes.setFieldValue("description", parsedData.description || "");
      } catch (error) {
        console.error("Error parsing notes data:", error);
      }
    }
  }, [notesQuery]);

  useEffect(() => {
    if (storeNotesResult.isSuccess) {
      toasts.success(
        "Notas actualizadas",
        "Se han actualizado las notas"
      );
    }
    if (storeNotesResult.isError) {
      toasts.danger(
        "Error",
        "No se han podido actualizar las notas"
      );
    }
  }, [ storeNotesResult]);


  return (
    <>
      <div className="mt-1 w-full">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-gray-200 pb-3 mb-5 md:mb-3 mt-4">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("lead.views.tasks")}
          </h1>

          <div className="flex justify-end gap-x-4 mt-4 md:mt-0">
            <div className="flex items-center gap-x-4">
              <Select
                name="task"
                selected={selectTask}
                setSelected={(task) => setSelectTask(task)}
                options={optionsTask}
                tittle={""}
                textDescribe=""
              />
            </div>
            <ButtonWhite
              text={t("share.views.generate_incident")}
              functionButton={() => {
                handleOpenDialogTask();
                setTitleDialogTask(t("share.views.generate_incident"));
                setTextButtonSubmitTask(
                  t("share.views.generate_incident")
                );
                setFormTask(1);
              }}
            />
            <div className="flex items-center gap-x-4">
              <ButtonAdd
                functionButton={() => {
                  handleOpenDialogTask();
                  setTitleDialogTask(t("lead.views.new_task"));
                  setTextButtonSubmitTask(t("lead.views.create_tasks"));
                  setFormTask(2);
                }}
              />
            </div>
            
          </div>
        </div>

        <Badges
          projects={useLazyAllTasks.data ?? []}
          isLoading={useLazyAllTasks.isLoading || useLazyAllTasks.isFetching}
          onEdit={(task) => {
            formikForm2.setFieldValue("name_task", task.task);
            formikForm2.setFieldValue("date_task", task.date);
            formikForm2.setFieldValue("support", task.assigned_id);

            handleOpenDialogTask();
            setTitleDialogTask(t("lead.views.new_task"));
            setTextButtonSubmitTask(t("lead.views.create_tasks"));
            setFormTask(2);
          }}
          completeTask={(id_task) => {
            completeTask({
              id: id_task
            });
          }}
        />
      </div>
      <div className="w-full">
        <h1 className="text-base font-semibold leading-6 text-gray-900 border-b border-gray-200 pb-3 mt-4  mb-5 md:mb-3">
          {t("user.views.notes")}
        </h1>
        <form onSubmit={formikNotes.handleSubmit} className="relative mt-4">
          <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
            <label htmlFor="title" className="sr-only">
              {t("lead.views.tittle")}
            </label>
            <input
              id="title"
              name="title"
              type="text"
              placeholder={t("lead.views.tittle")}
              className="block w-full border-0 pt-2.5 text-lg font-medium placeholder:text-gray-400 focus:ring-0"
              value={formikNotes.values.title}
              onChange={formikNotes.handleChange}
              onBlur={formikNotes.handleBlur}
            />
            <label htmlFor="description" className="sr-only">
              {t("lead.views.description")}
            </label>
            <textarea
              id="description"
              name="description"
              rows={8}
              placeholder={t("lead.views.write_description")}
              className="block w-full resize-none border-0 py-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              value={formikNotes.values.description}
              onChange={formikNotes.handleChange}
              onBlur={formikNotes.handleBlur}
            />

            <div aria-hidden="true">
              <div className="py-3">
                <div className="h-3" />
              </div>
              <div className="h-px" />
              <div className="py-3">
                <div className="py-px">
                  <div className="h-3" />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute inset-x-px bottom-0">
            <div className="flex items-center justify-end space-x-3 border-t border-gray-200 px-2 py-2 sm:px-3">
              <div className="flex-shrink-0">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  {t("general.save")}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <DialogCreate
        open={openDialogTask}
        setOpen={setOpenDialogTask}
        tittle={titleDialogTask}
        tittleButtonCreate={textButtonSubmitTask}
        tittleButtonCancel={t("general.cancel")}
        OnclickCreate={() => {
          if (formTask === 1) {
            formikForm1.handleSubmit();
          } else if (formTask === 2) {
            formikForm2.handleSubmit();
          }
        }}
        OnclickCancel={handleOpenDialogTask}
      >
        {formTask === 1 && (
          <form onSubmit={formikForm1.handleSubmit} className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("user.views.info_incident")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("lead.views.tittle")}
                      span={t("general.mandatory")}
                      id="title"
                      name="title"
                      type="text"
                      placeholder={t("user.views.name")}
                      value={formikForm1.values.title}
                      onChange={formikForm1.handleChange}
                      onBlur={formikForm1.handleBlur}
                      error={!!formikForm1.errors.title}
                      textError={formikForm1.errors.title}
                    />
                  </div>
                  <div className="col-span-full">
                    <SelectAvatar
                      tittle={t("share.views.assign")}
                      span={t("general.optional")}
                      selected={listStaff.find(
                        value => value.id == formikForm1.values.branch
                      )}
                      setSelected={(value) => formikForm1.setFieldValue('branch', value.id)}
                      options={listStaff}
                    />
                  </div>
                  <div className="col-span-full">
                    <TextArea
                      htmlFor="description"
                      label={t("bond.views.description")}
                      spanMessage={t("general.optional")}
                      value={formikForm1.values.description}
                      onChange={(value) =>
                        formikForm1.setFieldValue("description", value.target.value)
                      }
                      onBlur={formikForm1.handleBlur}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
        {formTask === 2 && (
          <form onSubmit={formikForm2.handleSubmit} className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("lead.views.info_task")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("lead.views.task")}
                      span={t("general.mandatory")}
                      id="name_task"
                      name="name_task"
                      type="text"
                      placeholder={t("user.views.name")}
                      value={formikForm2.values.name_task}
                      onChange={formikForm2.handleChange}
                      onBlur={formikForm2.handleBlur}
                      error={!!formikForm2.errors.name_task}
                      textError={formikForm2.errors.name_task}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("lead.views.expiration_date")}
                      span={t("general.mandatory")}
                      id="date_task"
                      name="date_task"
                      type="date"
                      value={formikForm2.values.date_task}
                      onChange={formikForm2.handleChange}
                      onBlur={formikForm2.handleBlur}
                      error={!!formikForm2.errors.date_task}
                      textError={formikForm2.errors.date_task}
                    />
                  </div>
                  <div className="col-span-full">
                    <SelectAvatar
                      tittle={t("share.views.assign")}
                      span={t("general.mandatory")}
                      selected={listStaff.find(
                        value => value.id == formikForm2.values.support
                      )}
                      setSelected={(value) => formikForm2.setFieldValue('support', value.id)}
                      options={listStaff}
                    />
                  </div>
                  <div className="col-span-full">
                    <MultiSelect
                      options={leadTasks}
                      selectedOptions={leadTasks.filter(option => formikForm2.values.labels_id.includes(option.id))}
                      setSelectedOptions={(selectedOptions) => {
                        const selectedIds = selectedOptions.map(option => option.id);
                        formikForm2.setFieldValue('labels_id', selectedIds);
                      }}
                      title={t("brand.views.tags")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </DialogCreate>

    </>
  );
}

export default LoyaltyUser;
